import { DAY_KEYS, photoLayoutFor, resolveShowTeam, todayKey } from '@/pages/public/booking-page/helpers';
import type { BookingPageBlock, BookingPagePhoto, DayKey, OpeningHours, TimeRange } from '@/pages/public/booking-page/types';
import type { BookingPageAdmin, BookingPageSettings } from './api';

export function emptyCustomHours(): Record<DayKey, TimeRange[]> {
  return DAY_KEYS.reduce((acc, day) => ({ ...acc, [day]: [] }), {} as Record<DayKey, TimeRange[]>);
}

export function customHoursToOpening(custom: Record<DayKey, TimeRange[]>, timeZone?: string, now: Date = new Date()): OpeningHours {
  const days = DAY_KEYS.map((day) => ({ day, ranges: custom[day] ?? [] }));
  const current = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(now);
  const open = (custom[todayKey(now, timeZone)] ?? []).find((r) => r.from <= current && current < r.to);
  return { days, open_now: !!open, closes_at: open?.to ?? null };
}

// The public `booking_page` block as it would look with the draft settings,
// for the live preview. Mirrors BookingPagePresenter#public_payload.
export function applyDraft(data: BookingPageAdmin, draft: BookingPageSettings, photos: BookingPagePhoto[], timeZone?: string): BookingPageBlock {
  const src = data.preview_source;
  const hours = draft.show_hours
    ? draft.hours_source === 'custom'
      ? customHoursToOpening(draft.custom_hours, timeZone)
      : src.team_hours
    : null;
  const location = draft.show_address && src.location
    ? {
        address: src.location.address,
        latitude: draft.show_map ? src.location.latitude : null,
        longitude: draft.show_map ? src.location.longitude : null,
        directions_url: draft.show_directions ? src.location.directions_url : null,
      }
    : null;

  return {
    tagline: draft.tagline.trim() || null,
    layout: photoLayoutFor(photos.length),
    photos,
    show_team: resolveShowTeam(draft.team_mode, src.team.length),
    team: src.team,
    about: src.about,
    show_online: draft.show_online,
    phone: draft.show_phone ? src.phone : null,
    email: src.email,
    hours,
    location,
  };
}

// The address customers use. Production subdomains; locally the slug route.
export function publicPageUrl(subdomain: string, path: 'book' | 'embed' = 'book', location: Pick<Location, 'hostname' | 'origin'> = window.location): string {
  if (location.hostname.endsWith('slotyapp.com')) {
    return path === 'book' ? `https://${subdomain}.slotyapp.com` : `https://${subdomain}.slotyapp.com/embed`;
  }
  return `${location.origin}/${subdomain}/${path}`;
}

export interface EmbedOptions {
  header: boolean;
  photos: boolean;
  sidebar: boolean;
  autoHeight: boolean;
}

export function embedUrl(subdomain: string, options: Omit<EmbedOptions, 'autoHeight'>, location?: Pick<Location, 'hostname' | 'origin'>): string {
  const flag = (v: boolean) => (v ? '1' : '0');
  return `${publicPageUrl(subdomain, 'embed', location)}?header=${flag(options.header)}&photos=${flag(options.photos)}&sidebar=${flag(options.sidebar)}`;
}

export function scriptUrl(location: Pick<Location, 'hostname' | 'origin'> = window.location): string {
  return location.hostname.endsWith('slotyapp.com') ? 'https://slotyapp.com/embed.js' : `${location.origin}/embed.js`;
}

export function buildEmbedSnippet(subdomain: string, orgName: string, options: EmbedOptions, location?: Pick<Location, 'hostname' | 'origin'>): string {
  const title = `Book with ${orgName}`.replace(/"/g, '&quot;');
  const iframe = [
    '<iframe',
    `  src="${embedUrl(subdomain, options, location)}"`,
    `  title="${title}"`,
    '  style="width:100%;min-height:900px;border:0"',
    '  loading="lazy"></iframe>',
  ].join('\n');
  return options.autoHeight ? `${iframe}\n<script src="${scriptUrl(location)}" async></script>` : iframe;
}
